import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { AdminLayout } from "./AdminLayout";
import api, { errMsg, openBlob } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, fmt } from "@/components/portal/PortalUI";
import { RecordDocuments, RecordRequests } from "@/components/portal/RecordDocs";
import { RecordMaterials, RecordQuizzes, RecordNotes, RecordActivity, RecordEdit } from "@/components/portal/RecordTraining";
import { ArrowLeft, ExternalLink } from "lucide-react";

const TABS = ["documents", "uploads", "materials", "quizzes", "progress notes", "activity", "notifications"];

export default function StudentRecord() {
  const { sid } = useParams();
  const { admin } = useAuth();
  const isAdmin = ["primary", "admin"].includes(admin?.role);
  const [d, setD] = useState(null);
  const [tab, setTab] = useState("documents");
  const load = useCallback(() => api.get(`/admin/students/${sid}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))), [sid]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <AdminLayout title="Student Record"><p className="text-dark-text/50">Loading…</p></AdminLayout>;
  const s = d.student;
  return (
    <AdminLayout title="Student Record">
      <Link to="/admin/students" className="inline-flex items-center gap-2 text-navy font-semibold text-sm mb-4 hover:text-magenta"><ArrowLeft size={16} /> Back to Students</Link>
      <div className="bg-navy text-white rounded-lg p-6 mb-5 relative overflow-hidden" data-testid="student-record-header">
        <div className="absolute right-0 top-0 h-full w-2 bg-magenta" />
        <div className="flex flex-wrap justify-between gap-4">
          <div>
            <h2 className="font-display text-2xl font-black" data-testid="record-name">{s.name}</h2>
            <p className="text-white/60 text-sm font-mono">{s.reference_number} · {s.email}</p>
            <div className="flex flex-wrap gap-2 mt-3">{s.programs.map((p) => <span key={p} className="bg-white/10 text-xs font-semibold px-3 py-1 rounded-full">{p}</span>)}</div>
          </div>
          <div className="text-sm space-y-1 text-white/80">
            <p>Status: <strong className="text-magenta" data-testid="record-status">{s.status}</strong> · {s.registration_status}</p>
            <p>Cohort: {s.cohort || "—"} · Instructor: {s.instructor_name || s.instructor_email || "—"}</p>
            <p>Mentor: {s.mentor_name || s.mentor_email || "—"} · Enrolled {fmt(s.enrolled_at)}</p>
            <p>Last login: {fmt(s.last_login_at)}</p>
            {isAdmin && <Link to={`/admin/applicants/${s.applicant_id}`} className="inline-flex items-center gap-1 text-magenta font-bold" data-testid="record-open-applicant"><ExternalLink size={14} /> Full applicant history</Link>}
            <div className="flex flex-wrap gap-3 pt-1" data-testid="record-certificate">
              {s.certificate && <button onClick={() => openBlob(`/admin/students/${s.student_id}/certificate`)} className="text-white font-bold underline text-xs" data-testid="record-cert-view">Certificate {s.certificate.cert_id}</button>}
              {isAdmin && (s.status === "Completed" || s.certificate) && <button onClick={async () => { try { await api.post(`/admin/students/${s.student_id}/certificate`); toast.success(s.certificate ? "Certificate reissued" : "Certificate issued"); load(); } catch (e) { toast.error(errMsg(e)); } }} className="bg-magenta text-white text-xs font-bold px-3 py-1 rounded" data-testid="record-cert-issue">{s.certificate ? "Reissue Certificate" : "Issue Certificate"}</button>}
            </div>
          </div>
        </div>
      </div>
      {isAdmin && <RecordEdit s={s} reload={load} />}
      <div className="flex gap-1 overflow-x-auto border-b border-navy/10 mb-5">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`record-tab-${t.replace(" ", "-")}`}
            className={`px-4 py-3 text-sm font-semibold capitalize whitespace-nowrap border-b-2 transition-colors ${tab === t ? "border-magenta text-magenta" : "border-transparent text-dark-text/60 hover:text-navy"}`}>{t}</button>
        ))}
      </div>
      {tab === "documents" && <RecordDocuments docs={d.documents} reload={load} isAdmin={isAdmin} />}
      {tab === "uploads" && <RecordRequests sid={sid} requests={d.requests} reload={load} />}
      {tab === "materials" && <RecordMaterials items={d.materials} />}
      {tab === "quizzes" && <RecordQuizzes items={d.quizzes} reload={load} canManage={admin?.role !== "mentor"} />}
      {tab === "progress notes" && <RecordNotes sid={sid} notes={d.progress_notes} reload={load} />}
      {tab === "activity" && <RecordActivity items={d.activity} />}
      {tab === "notifications" && (
        <Card title="Portal Notifications Sent">
          <ul className="divide-y divide-navy/5 text-sm">{d.notifications.map((n) => <li key={n.id} className="py-2 flex justify-between gap-3"><span><strong className="text-navy">{n.title}</strong> — {n.message}</span><span className="text-xs text-dark-text/50 whitespace-nowrap">{fmt(n.created_at)} {n.read ? "· read" : ""}</span></li>)}</ul>
        </Card>
      )}
    </AdminLayout>
  );
}
